import React from 'react';
import type { FC } from 'react';

import {
  Document,
  Page,
  Text,
  View,
  Svg,
  Path,
  Defs,
  Stop,
  Rect,
  G,
  ClipPath,
  LinearGradient,
  StyleSheet,
} from '@react-pdf/renderer';

import moment from 'moment';

import { TargetsTypes } from 'Utils/constants';

import type { CampaignDetailsType } from 'types';

const styles = StyleSheet.create({
  page: {
    fontSize: '14px',
    backgroundColor: '#F8F8F8',
    padding: '17px 20px',
  },
  header: {
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
  },
  downloaded_at: {
    marginBottom: '10px',
  },
  downloaded_at_text: {
    fontSize: '12px',
    color: '#989898',
  },
  campaign: {
    width: '100%',
    backgroundColor: '#FFFFFF',
    padding: '13px 16px',
    borderRadius: '20px',
    flexDirection: 'column',
  },
  // -------- header -------
  campaign_header: {
    width: '100%',
    paddingBottom: '9px',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: '1px solid #DEDEDE',
  },
  campaign_name: {
    color: '#7E7E7E',
    fontSize: '17px',
    lineHeight: '1',
  },
  // Campaign in Review
  CampaignBadge_inReview: {
    backgroundColor: '#fff9ec',
    border: '1px solid #ffab03',
    padding: '6px',
    flexDirection: 'row',
  },
  CampaignBadge_inReview_circle: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: '#FFAB00',
    marginRight: '7px',
  },
  CampaignBadge_inReview_text: {
    fontSize: '8px',
    color: '#ffab03',
  },
  // Campaign draft
  CampaignBadge_draft: {
    backgroundColor: '#fafafa',
    border: '1px solid #aab2b5',
    padding: '6px',
    flexDirection: 'row',
  },
  CampaignBadge_draft_circle: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: '#aab2b5',
    marginRight: '7px',
  },
  CampaignBadge_draft_text: {
    fontSize: '8px',
    color: '#6d797c',
  },
  // Campaign active
  CampaignBadge_active: {
    backgroundColor: '#f4fff2',
    border: '1px solid #2ad90e',
    padding: '6px',
    flexDirection: 'row',
  },
  CampaignBadge_active_circle: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: '#2ad90e',
    marginRight: '7px',
  },
  CampaignBadge_active_text: {
    fontSize: '8px',
    color: '#2ad90e',
  },
  // ------- Details ---------
  campaign_details: {
    padding: '12px 9px 0 9px',
  },
  // ----- Audiences --------
  audiences: {
    flexDirection: 'column',
    marginBottom: '20px',
  },
  individual_audience: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px',
    backgroundColor: '#F8F8F8',
    borderRadius: '12px',
    marginBottom: '10px',
  },
  individual_audience_left: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  individual_audience_targetview: {
    width: '18px',
    height: '18px',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  individual_audience_nameview: {
    marginLeft: '6px',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  individual_audience_name: {
    color: '#282828',
    fontSize: '10px',
  },
  individual_audience_right: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  individual_audience_personview: {
    width: '18px',
    height: '18px',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  individual_audience_prospectsview: {
    marginLeft: '6px',
  },
  individual_audience_prospects: {
    color: '#282828',
    fontSize: '10px',
  },
  // ----- Campaign Info -------
  campaign_info: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  // ------ Campaign info left side --------
  campaign_info_left: {
    flexDirection: 'column',
    justifyContent: 'flex-start',
  },
  campaign_info_creatives: {
    flexDirection: 'column',
    justifyContent: 'flex-start',
    marginBottom: '14px',
  },
  campaign_info_creatives_label: {
    marginBottom: '4px',
  },
  campaign_info_creatives_label_text: {
    fontSize: '12px',
    color: '#989898',
    fontWeight: 500,
  },
  campaign_info_creatives_value_text: {
    fontSize: '13px',
    color: '#282828',
    fontWeight: 400,
  },
  // ---------- Campaign info right side ----------
  campaign_info_days: {
    width: '50%',
  },
  campaign_info_days_wrapper: {
    flexDirection: 'column',
    justifyContent: 'flex-start',
    backgroundColor: '#F8F8F8',
    padding: '17px 20px',
    borderRadius: '12px',
  },
  campaign_info_days_header: {
    flexDirection: 'column',
    justifyContent: 'flex-start',
    marginBottom: '9px',
  },
  campaign_info_days_header_label: {
    fontSize: '12px',
    color: '#282828',
  },
  campaign_info_days_header_value: {
    fontSize: '13px',
    color: '#999999',
  },
  campaign_info_days_progress_meter: {
    width: '100%',
    height: '15px',
    backgroundColor: '#EEEFF0',
    borderRadius: '30px',
  },
  campaign_info_days_progress_percent: {
    height: '100%',
    borderRadius: '30px',
  },
  start_end_date: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: '8px',
  },
  flight_text: {
    color: '#282828',
    fontSize: '8px',
  },
  // ---------- Report ---------
  campaign_report: {
    marginTop: '12px',
  },
  campaign_report_block: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  campaign_report_block_sm: {
    padding: '9px 14px',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    width: '28%',
    backgroundColor: '#F9F9F9',
    borderRadius: '12px',
  },
  campaign_report_block_sm_icon: {
    width: '29px',
    height: '29px',
  },
  campaign_report_block_sm_clicks: {
    flexDirection: 'column',
    marginLeft: '10px',
  },
  campaign_report_block_sm_clicks_count: {
    color: '#000000',
    fontSize: '20px',
    fontWeight: 400,
  },
  campaign_report_block_sm_clicks_title: {
    color: '#7E7E7E',
    fontSize: '14px',
    fontWeight: 400,
  },
  campaign_report_block_lg: {
    padding: '10px 24px 10px 18px',
    flexDirection: 'column',
    width: '100%',
    backgroundColor: '#F9F9F9',
    borderRadius: '12px',
  },
  campaign_report_total: {
    fontSize: '14px',
    color: '#000000',
  },
  campaign_report_details: {
    color: '#7E7E7E',
    fontSize: '8px',
  },
  campaign_creatives: {
    flexDirection: 'column',
    marginTop: '12px',
  },
  campaign_individual_creative: {
    flexDirection: 'column',
    padding: '12px',
    backgroundColor: '#F8F8F8',
    borderRadius: '12px',
    marginBottom: '10px',
  },
  campaign_individual_creative_name_wr: {
    marginBottom: '15px',
    flexDirection: 'row',
  },
  campaign_individual_creative_name: {
    fontSize: '14px',
  },
  campaign_individual_creative_stats: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
  },
  campaign_individual_creative_stats_ind: {
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: '15px',
  },
  campaign_individual_creative_stats_ind_icon: {
    width: '20px',
    height: '20px',
    marginRight: '12px',
  },
  campaign_individual_creative_stats_ind_content: {
    flexDirection: 'row',
  },
  campaign_individual_creative_stats_ind_title: {
    color: '#7E7E7E',
    fontSize: '14px',
    fontWeight: 400,
    marginRight: '2px',
  },
  campaign_individual_creative_stats_ind_value: {
    color: '#000000',
    fontSize: '14px',
    fontWeight: 400,
  },
});

const TargetSVG = () => (
  <Svg width="39" height="39" viewBox="0 0 39 39">
    <Path
      fillRule="evenodd"
      d="M27.5135 22.1314C29.2412 18.2404 28.5118 13.5208 25.3195 10.3305C21.1793 6.19046 14.4683 6.19046 10.3276 10.3305C6.18923 14.4705 6.18923 21.1812 10.3276 25.3216C13.5199 28.5119 18.236 29.2413 22.1291 27.5155L18.4151 23.7998C16.6529 23.973 14.8725 23.3609 13.5796 22.0702C11.2361 19.7268 11.2361 15.9244 13.5796 13.5788C15.9232 11.2354 19.7258 11.2354 22.0692 13.5788C23.362 14.8715 23.9741 16.6536 23.7988 18.414L27.5135 22.1314ZM23.0356 34.8763C21.387 35.3801 19.6375 35.651 17.8247 35.651C16.012 35.651 14.2625 35.3801 12.6138 34.8763L12.6048 34.8726L12.3572 34.795L12.2218 34.7499C12.1408 34.7245 12.0595 34.6958 11.9782 34.6668L11.8718 34.6308C11.7868 34.6 11.7019 34.5692 11.617 34.5386L11.5106 34.4987C11.3319 34.4301 11.1548 34.36 10.9798 34.2878L10.9399 34.2714C10.8588 34.237 10.7812 34.2028 10.7017 34.1684L10.5826 34.1161C10.5103 34.0856 10.4399 34.0529 10.3713 34.0206L10.2177 33.9483L10.0101 33.8489L9.89097 33.7912C9.63101 33.6612 9.3765 33.5258 9.12349 33.3831L9.02404 33.329L8.80036 33.199L8.69759 33.1377C8.62354 33.0941 8.54948 33.0491 8.47723 33.0041L8.37053 32.9391C8.29829 32.8941 8.22786 32.8487 8.15562 32.8019L8.04559 32.7296C7.89566 32.6302 7.74785 32.5292 7.59974 32.4262L7.55984 32.399C7.48578 32.3467 7.41384 32.2944 7.3416 32.2421L7.25122 32.1771L7.049 32.0254L6.93897 31.9386L6.75095 31.7923L6.64636 31.7077C6.58138 31.6551 6.51639 31.6046 6.45321 31.5523C6.36283 31.4765 6.27457 31.4024 6.18601 31.3266L5.99436 31.157L5.90972 31.0811L5.73108 30.9185L5.60806 30.803L5.46538 30.6694L5.31726 30.5231L5.18185 30.3914C5.12048 30.33 5.06094 30.265 4.99957 30.2037L4.92189 30.1242L4.83363 30.0287L4.671 29.8537L4.53196 29.6998L4.40561 29.5571L4.27926 29.4111L4.15291 29.263L4.02475 29.1059L3.8981 28.9505L3.78444 28.806C3.70676 28.7105 3.6327 28.6111 3.55864 28.5137L3.46131 28.3801L3.33315 28.2048L3.22101 28.048L3.10373 27.8781L3.00096 27.7267L2.87794 27.5405L2.78574 27.396C2.71924 27.2914 2.65426 27.1884 2.58926 27.082L2.53153 26.9861L2.38704 26.7407L2.30936 26.6089L2.18482 26.3813L2.11802 26.2568L1.99348 26.0241L1.93001 25.8992C1.87953 25.8019 1.83086 25.7043 1.78401 25.6051L1.75318 25.5456C1.69544 25.4229 1.63771 25.298 1.58179 25.1753L1.55096 25.1067C1.50592 25.0094 1.46421 24.9118 1.42098 24.8126L1.37413 24.6987C1.33604 24.6086 1.29826 24.5164 1.26199 24.4242L1.21876 24.3142C1.18098 24.2184 1.14471 24.1228 1.10873 24.027L1.07428 23.9333C1.02742 23.8049 0.982083 23.6767 0.937047 23.5468L0.926165 23.5126C0.888381 23.3986 0.850295 23.2832 0.814326 23.1677L0.776542 23.0468C0.747524 22.9528 0.720621 22.8591 0.693419 22.7669L0.659261 22.6478C0.630243 22.5451 0.601527 22.4402 0.574319 22.3374L0.552556 22.2561C0.48394 21.9889 0.420767 21.7163 0.364843 21.4436L0.344893 21.346C0.32313 21.2396 0.303482 21.1347 0.285346 21.0302L0.265396 20.9238C0.245446 20.8156 0.227612 20.7071 0.21129 20.597L0.19678 20.5048C0.176831 20.3767 0.158996 20.2485 0.142673 20.1204L0.139046 20.0914C0.120909 19.9469 0.103076 19.8024 0.09038 19.6579L0.0849391 19.6092C0.0740577 19.4883 0.0631753 19.3674 0.0522931 19.2462L0.0432249 19.1108C0.0359704 19.0132 0.0287157 18.9159 0.025088 18.8164L0.0178335 18.6865C0.0123925 18.5801 0.00876551 18.4734 0.00513863 18.367L0.00151136 18.2533C0.00181369 18.1106 0 17.968 0 17.8253L0.00181363 17.6899C0.00181363 17.6014 0.00181371 17.5131 0.0054409 17.4263L0.0108818 17.2187L0.01995 17.0038L0.0308324 16.7925L0.0435272 16.5976L0.0598505 16.3772L0.0779863 16.1678L0.0961232 15.9728C0.110632 15.8446 0.125141 15.7147 0.139348 15.5883L0.161111 15.4239L0.195268 15.1821L0.222472 15.0053L0.260256 14.7704L0.292902 14.5863L0.3325 14.3805L0.370284 14.1855C0.388421 14.1006 0.406254 14.0175 0.424391 13.9347L0.487566 13.662C0.507516 13.5825 0.52535 13.503 0.5453 13.4239L0.592152 13.2452L0.646259 13.043L0.700365 12.8517L0.763541 12.644L0.817648 12.4654L0.880823 12.2723L0.94037 12.0882C0.978154 11.9745 1.01805 11.8642 1.05765 11.7524L1.06128 11.7415L1.13533 11.5465L1.20214 11.3697L1.27801 11.182L1.35206 11.0016L1.43519 10.8048L1.50743 10.6386L1.58874 10.4545C1.64103 10.3408 1.69333 10.2287 1.74592 10.1169L1.8091 9.98689C1.84688 9.91284 1.88315 9.83879 1.92094 9.76473L2.01313 9.58791L2.09807 9.42711L2.19933 9.23941L2.28427 9.08586L2.39611 8.89091L2.4611 8.78088C2.54241 8.64547 2.62373 8.51006 2.70836 8.37465L2.77879 8.26282C2.81657 8.20146 2.85828 8.1401 2.89789 8.07874L2.99371 7.93426C3.03331 7.87472 3.07502 7.81517 3.11643 7.75382L3.21043 7.6184L3.34222 7.4307L3.4199 7.3225C3.46857 7.25389 3.51936 7.18709 3.56983 7.12029C3.64026 7.02629 3.71069 6.9344 3.78293 6.84402L3.78475 6.8404L3.91654 6.67597L4.01931 6.54963L4.17467 6.36192L4.26324 6.25553L4.42586 6.06964L4.5108 5.97202C4.56672 5.90884 4.62446 5.84568 4.68219 5.78251L4.72541 5.73565C4.86627 5.58392 5.00895 5.43581 5.15343 5.28801L5.17156 5.26987C5.23655 5.20489 5.30335 5.13809 5.37016 5.0731L5.4424 5.00449L5.62467 4.82948L5.72926 4.73186L5.91515 4.56229L5.97651 4.50819C6.20413 4.30597 6.43687 4.10739 6.67536 3.91788L6.74579 3.86196C6.81985 3.80423 6.89209 3.7465 6.96615 3.68877L7.0384 3.63466C7.11064 3.58056 7.1847 3.52464 7.25876 3.47023L7.34188 3.40888C7.5964 3.22299 7.85453 3.04586 8.12026 2.87267L8.15442 2.84909L8.3778 2.70975L8.47543 2.6484C8.55311 2.60154 8.63261 2.55258 8.71211 2.50573L8.7771 2.46795C8.86385 2.41747 8.9503 2.36518 9.03706 2.31621L9.0697 2.29808C9.34417 2.14272 9.62225 1.99643 9.90581 1.85588L10.0034 1.80722L10.2383 1.69357L10.3465 1.6449C10.4315 1.60712 10.5161 1.56722 10.6028 1.52944L10.6551 1.50586C10.9601 1.37226 11.2691 1.24411 11.585 1.12865L11.6067 1.11958C11.6971 1.08542 11.7872 1.05459 11.8776 1.02013L11.977 0.984161C12.0638 0.953331 12.1502 0.92643 12.2388 0.895599L12.331 0.864773C14.0606 0.303177 15.9078 0 17.8254 0C19.743 0 21.5881 0.303463 23.3198 0.864773L23.4138 0.895599C23.5005 0.92643 23.5888 0.953335 23.6756 0.984161L23.775 1.02013L24.0477 1.11958L24.0694 1.12865C24.3799 1.2423 24.687 1.36864 24.9883 1.50223L25.0551 1.53125C25.1364 1.56903 25.2196 1.6053 25.3006 1.6449L25.4161 1.69719L25.6491 1.80902L25.7486 1.85769C26.0266 1.99673 26.3029 2.14121 26.5737 2.29475L26.617 2.32014L26.8751 2.47006L26.9401 2.50784C27.0196 2.55469 27.0991 2.60366 27.1783 2.65051L27.2759 2.71187C27.3518 2.75872 27.4295 2.80949 27.5053 2.85635L27.5395 2.87992C27.8031 3.05312 28.063 3.23024 28.3194 3.41613L28.4025 3.47749L28.6174 3.63829L28.7023 3.70327L28.9046 3.86226L29.0091 3.94538C29.2349 4.12764 29.455 4.31383 29.6699 4.50697L29.7458 4.57377L29.9244 4.73639L30.0399 4.84822L30.2043 5.00358L30.3071 5.10302C30.3648 5.15894 30.4226 5.21668 30.4803 5.2744L30.5616 5.35752C30.6789 5.47842 30.7983 5.60295 30.9138 5.72779L30.9769 5.7964C31.031 5.85595 31.087 5.91549 31.1396 5.97685L31.2408 6.0905L31.3871 6.26188L31.4899 6.38097L31.6398 6.56141L31.7411 6.68776L31.8729 6.85037L31.8747 6.85581C31.9469 6.948 32.0174 7.03989 32.0878 7.13207L32.2141 7.30345L32.3223 7.45337L32.436 7.61236L32.5533 7.78192L32.6561 7.93365L32.7788 8.12136L32.871 8.26403C32.916 8.33627 32.9632 8.40851 33.0082 8.48256C33.0569 8.56387 33.1077 8.64698 33.1563 8.72799L33.2663 8.91207L33.3585 9.07286L33.4649 9.2642L33.5499 9.41955L33.6581 9.62358L33.7303 9.76262C33.779 9.86025 33.828 9.95576 33.8748 10.0552L33.9056 10.1147C33.9634 10.2338 34.0193 10.3529 34.0737 10.4723L34.1315 10.6023C34.1656 10.6763 34.1964 10.7504 34.2291 10.8263L34.2977 10.9889L34.3881 11.2092L34.4494 11.3628C34.4785 11.4386 34.5072 11.5163 34.5362 11.5922L34.5903 11.7367C34.6335 11.8558 34.6771 11.9767 34.7166 12.0979L34.7617 12.2369L34.8394 12.4751L34.8899 12.6431L34.9585 12.8725L35.0035 13.0333L35.0703 13.286L35.1063 13.4196C35.1353 13.5278 35.1604 13.6363 35.1858 13.7445L35.2021 13.8095L35.2057 13.8204C35.2347 13.9486 35.2616 14.0767 35.2888 14.2049L35.3179 14.353C35.336 14.4397 35.3538 14.5262 35.3683 14.6148L35.3955 14.761C35.4119 14.8532 35.4264 14.9469 35.4406 15.0391L35.4623 15.1745C35.4787 15.2791 35.4932 15.3804 35.5038 15.4831L35.5183 15.5844C35.5364 15.7198 35.5524 15.8552 35.5651 15.9943L35.576 16.1152C35.5851 16.211 35.5941 16.3047 35.6032 16.4023L35.6159 16.5791L35.6304 16.8246L35.6413 16.9959C35.6449 17.0845 35.6485 17.1746 35.6522 17.2631L35.6558 17.4239C35.6594 17.5612 35.6612 17.6984 35.6612 17.8374C35.6612 17.9765 35.6594 18.1137 35.6558 18.2509L35.6522 18.4099C35.6485 18.5003 35.6449 18.5903 35.6413 18.6771L35.6304 18.8485L35.6159 19.0921L35.6032 19.2707C35.5941 19.3665 35.5851 19.4621 35.576 19.5579L35.5651 19.6788C35.5506 19.816 35.5343 19.9532 35.5183 20.0904L35.5056 20.1826C35.4911 20.289 35.4766 20.3939 35.4623 20.4985L35.4406 20.6321L35.3955 20.912L35.3683 21.0583L35.3179 21.32L35.2888 21.4663C35.2616 21.5963 35.2347 21.7244 35.2057 21.8508L35.2021 21.8617L35.1858 21.9266C35.1604 22.0367 35.1353 22.1452 35.1063 22.2534L35.0721 22.3815L35.0017 22.6433L34.9585 22.7987L34.888 23.0335L34.8412 23.1925C34.8122 23.2811 34.7834 23.3693 34.7544 23.4561L34.7166 23.5679C34.6752 23.6906 34.6317 23.8115 34.5885 23.9327L34.5344 24.0772C34.5054 24.1549 34.4766 24.2326 34.4458 24.3084L34.3899 24.4547L34.2923 24.6878L34.2212 24.8268L34.1744 24.935L30.6481 25.2617L29.8643 24.4801L29.8897 24.435L29.9275 24.3646C29.9743 24.2761 30.0233 24.1878 30.0683 24.0992L30.0955 24.0451C30.1333 23.9711 30.1696 23.897 30.2056 23.823L30.2433 23.7489C30.2811 23.6712 30.3156 23.5936 30.3516 23.5159L30.3733 23.4708C30.4147 23.3787 30.4564 23.285 30.496 23.1909L30.4997 23.1783L30.5051 23.1692C30.5429 23.0788 30.5792 22.9887 30.6169 22.8984L30.6496 22.8152L30.7218 22.6257L30.7651 22.5066L30.83 22.3226L30.8787 22.1745C30.9041 22.0986 30.9274 22.0227 30.9528 21.9469L30.9854 21.8456L31.0395 21.6543L31.0737 21.5388L31.1314 21.3275L31.1532 21.2462C31.1786 21.1504 31.2 21.0549 31.2236 20.9573L31.2472 20.8545L31.2904 20.6505L31.3122 20.5441L31.3554 20.3219L31.3717 20.2334C31.388 20.1357 31.4059 20.0366 31.4204 19.939L31.4349 19.8362L31.4639 19.634L31.4802 19.5058L31.5038 19.3145L31.5165 19.19C31.5274 19.0941 31.5346 18.9986 31.5437 18.901L31.5455 18.8829L31.5473 18.836C31.5546 18.7511 31.56 18.668 31.5655 18.5833L31.5709 18.4552L31.5782 18.2747L31.5836 18.123L31.5854 17.9244V17.825C31.5854 17.7131 31.5854 17.6046 31.5818 17.4946L31.58 17.4278C31.5782 17.3356 31.5727 17.2474 31.5691 17.1588L31.5655 17.0884C31.5546 16.868 31.5365 16.6513 31.5168 16.4349L31.5114 16.3826C31.5023 16.294 31.4896 16.204 31.4787 16.1172L31.4697 16.0432C31.457 15.951 31.4443 15.8591 31.4319 15.7687L31.4246 15.7237C31.4083 15.6173 31.3905 15.5124 31.3741 15.4078L31.3557 15.348C31.3394 15.2612 31.3231 15.1748 31.307 15.088L31.2907 15.0158C31.2726 14.9291 31.2529 14.8444 31.2348 14.7577L31.2239 14.7018C31.1752 14.4959 31.1211 14.2901 31.0649 14.0861L31.045 14.0229C31.0196 13.9361 30.9963 13.8497 30.9691 13.7648L30.9546 13.7179C30.9219 13.6167 30.8914 13.5175 30.857 13.4181L30.8551 13.4126C30.8192 13.3114 30.7847 13.2141 30.7487 13.1128L30.727 13.0587L30.6402 12.8311L30.6094 12.7534C30.5317 12.5603 30.4504 12.3671 30.3658 12.1791L30.344 12.1323C30.3062 12.0528 30.2699 11.9733 30.2322 11.8941L30.2031 11.8364C30.1599 11.7478 30.1146 11.6577 30.0714 11.571L30.0677 11.5637C30.0191 11.4679 29.9683 11.376 29.9178 11.282L29.8924 11.2388C29.8528 11.1648 29.8111 11.0907 29.7715 11.0185L29.7301 10.9462L29.6074 10.7368L29.5732 10.6808C29.5209 10.5941 29.4668 10.5095 29.4124 10.4245L29.3852 10.3849C29.3383 10.3109 29.2876 10.2368 29.2407 10.1646L29.2029 10.1087C29.1579 10.0401 29.1089 9.97325 29.0621 9.90464L29.0243 9.85054C28.9052 9.68248 28.7807 9.51835 28.6558 9.35756L28.618 9.31071C28.5676 9.24753 28.5168 9.18436 28.4663 9.123L28.4158 9.06164C28.2877 8.90628 28.154 8.75304 28.0186 8.60494L27.97 8.55083C27.9177 8.4931 27.8636 8.43537 27.8092 8.37764L27.755 8.31991C27.6864 8.24948 27.6196 8.18087 27.551 8.11226L27.5474 8.10863C27.4751 8.03458 27.4011 7.96234 27.3252 7.89191L27.2766 7.84506C27.2206 7.79277 27.1629 7.73685 27.1033 7.68426L27.042 7.62834L26.8597 7.46755L26.8111 7.42432C26.737 7.36115 26.6629 7.29798 26.5889 7.23662L26.5203 7.1807L26.3489 7.04348L26.2784 6.98756C26.2153 6.9389 26.1503 6.88811 26.0853 6.83945L26.0403 6.80349C25.9608 6.74575 25.8794 6.68802 25.8002 6.62848L25.7226 6.57619L25.5545 6.45892L25.4605 6.39756L25.2909 6.28572L25.2024 6.22799L25.0146 6.11253L24.9551 6.07475H24.9533L24.901 6.0421C24.836 6.00251 24.7692 5.96079 24.7006 5.92301L24.6102 5.87253L24.4298 5.77309L24.343 5.72624L24.1263 5.61259L24.0849 5.58901L24.0667 5.57994C23.9818 5.53672 23.8972 5.49319 23.8122 5.4536L23.7128 5.40857L23.5447 5.32907L23.431 5.27859L23.2234 5.18641L23.162 5.16102C21.5207 4.46583 19.717 4.08315 17.823 4.08315C15.9308 4.08315 14.1289 4.46581 12.4876 5.15738L12.4063 5.19335L12.2149 5.27647L12.0995 5.32876L11.9314 5.40825L11.8338 5.45329C11.747 5.4947 11.6624 5.53822 11.5756 5.57963L11.4998 5.61922C11.433 5.65338 11.3662 5.68784 11.3012 5.72562L11.2126 5.77247L11.0464 5.86647L10.9381 5.92783C10.875 5.96379 10.8136 6.00006 10.7504 6.03966L10.6909 6.07563L10.6295 6.11341L10.4418 6.22887L10.3532 6.28661L10.1837 6.39844L10.0897 6.46161L9.94156 6.56438L9.8134 6.64569C9.74841 6.69254 9.68161 6.7415 9.61843 6.78835L9.548 6.84064L9.37994 6.97062L9.28956 7.04104L9.1487 7.1547L9.05288 7.23418C8.98245 7.29192 8.91383 7.34965 8.84522 7.40919L8.76934 7.47599L8.61579 7.61321L8.53448 7.68545L8.37548 7.83537L8.32137 7.88403C8.24913 7.95264 8.17689 8.02307 8.10646 8.09349L8.06686 8.1349L7.90424 8.30114L7.83018 8.38063L7.67935 8.54143L7.62706 8.59735C7.49164 8.74727 7.35985 8.899 7.23168 9.05406L7.17939 9.11723L7.04034 9.29042L6.98261 9.36266C6.92669 9.4349 6.87077 9.50714 6.81636 9.58119L6.80367 9.59752L6.79641 9.60658C6.73686 9.68607 6.67913 9.76557 6.6214 9.84658L6.58361 9.90068L6.44094 10.1047L6.40315 10.1606C6.35449 10.2329 6.30552 10.3069 6.25866 10.381L6.23146 10.4206C6.17735 10.5055 6.12506 10.5901 6.07246 10.6769L6.03831 10.7328L5.91377 10.9423L5.87236 11.0163L5.76415 11.2076L5.71911 11.289C5.67226 11.3757 5.62692 11.464 5.58188 11.5507L5.55226 11.6109C5.51629 11.6813 5.48002 11.7517 5.44586 11.8221L5.40263 11.9107C5.36848 11.9847 5.33402 12.057 5.30137 12.131L5.27779 12.1815C5.23638 12.2737 5.19467 12.3674 5.15507 12.4596L5.15145 12.4723L5.14782 12.4814C5.11004 12.5717 5.07376 12.6618 5.03598 12.7522L5.00333 12.8353L4.92746 13.0369L4.89331 13.1291C4.86247 13.2122 4.83376 13.2953 4.80655 13.3782L4.79386 13.4159L4.79205 13.4214L4.78842 13.4341C4.75759 13.5245 4.72706 13.6163 4.69985 13.7067L4.67446 13.7898C4.65089 13.8675 4.62761 13.9452 4.60403 14.0229L4.58408 14.0915C4.56051 14.1782 4.53723 14.2647 4.51366 14.3514L4.50277 14.391L4.49733 14.4146C4.47375 14.5068 4.45048 14.6005 4.42872 14.6927L4.40695 14.7954L4.36191 14.9977L4.34015 15.1095C4.32383 15.1872 4.30932 15.2667 4.29511 15.3462L4.28242 15.4075C4.26428 15.5121 4.24645 15.617 4.23194 15.7234L4.22468 15.7738C4.21199 15.8588 4.19929 15.9434 4.18871 16.0284L4.17602 16.1402L4.15063 16.3496L4.13975 16.4509C4.13068 16.5522 4.1198 16.6513 4.11254 16.7526L4.11073 16.7725L4.10891 16.8194C4.10166 16.9043 4.09622 16.9889 4.09259 17.0757L4.08715 17.1875L4.07808 17.4078L4.07627 17.5179C4.07445 17.6225 4.07264 17.7273 4.07264 17.8319C4.07264 17.9383 4.07445 18.0414 4.07627 18.146L4.07808 18.256L4.08715 18.4781L4.09259 18.5882C4.09622 18.6731 4.10166 18.7595 4.10891 18.8445L4.11073 18.8913L4.11254 18.9095C4.1198 19.0107 4.12887 19.1099 4.13975 19.2111L4.15063 19.3106L4.17602 19.5237L4.18871 19.6319C4.19959 19.7186 4.21229 19.8051 4.22468 19.8918L4.23194 19.9368C4.24826 20.0432 4.2661 20.1499 4.28423 20.2563L4.2933 20.3068L4.34015 20.554L4.36192 20.6604L4.40695 20.8645L4.43053 20.9672C4.45411 21.0649 4.47557 21.1604 4.50096 21.2562L4.5191 21.323L4.5859 21.5648L4.60766 21.6425L4.67628 21.8683L4.70167 21.9532C4.7325 22.049 4.76303 22.1427 4.79386 22.2385L4.80293 22.2657C4.83376 22.3525 4.86429 22.4407 4.89512 22.5275L4.93109 22.6269L5.00696 22.8255L5.03961 22.9086C5.07739 22.999 5.11367 23.0891 5.15145 23.1795L5.15508 23.1885L5.1587 23.2012C5.1983 23.2952 5.24002 23.3889 5.28142 23.4811L5.30137 23.5261C5.33734 23.6038 5.37362 23.6815 5.40958 23.7592L5.44555 23.8332L5.55558 24.0554L5.58279 24.1095C5.62783 24.1981 5.67498 24.2863 5.72365 24.3749L5.75992 24.435L5.86813 24.6282L5.91498 24.7095C5.95851 24.7835 6.00173 24.8576 6.04707 24.9316L6.06884 24.9676C6.12294 25.058 6.17886 25.1462 6.2369 25.2348L6.24597 25.2511C6.29826 25.3324 6.35237 25.4156 6.40859 25.4966L6.44124 25.5434C6.48628 25.612 6.53343 25.6788 6.5821 25.7456L6.62532 25.807C6.68306 25.8865 6.74079 25.9678 6.79853 26.0452L6.80366 26.0545L6.81636 26.0708C6.87047 26.1449 6.92639 26.2171 6.9823 26.2894L7.04004 26.3616L7.17909 26.533L7.23138 26.598C7.35954 26.7533 7.49133 26.9048 7.62675 27.0547L7.67542 27.1088L7.83804 27.2823L7.89578 27.3436C7.95351 27.4032 8.01125 27.4627 8.07079 27.5223L8.10858 27.5601C8.18082 27.6305 8.25125 27.7009 8.32349 27.7713L8.37397 27.8182L8.54052 27.972L8.61276 28.037L8.78233 28.1869L8.83825 28.2356C8.91231 28.3006 8.98637 28.3619 9.06224 28.4251L9.13085 28.4792L9.29348 28.611L9.38204 28.6814L9.54829 28.8078L9.61872 28.8601C9.69428 28.9199 9.77378 28.9795 9.85147 29.0336L9.9092 29.0732C9.97419 29.1182 10.041 29.1653 10.106 29.2104L10.171 29.2536L10.375 29.389L10.4327 29.4268C10.5159 29.4809 10.6008 29.5332 10.6873 29.584L10.6927 29.5876C10.7794 29.6402 10.8662 29.6925 10.9526 29.7414L11.0303 29.7865L11.218 29.8911L11.303 29.9382L11.5016 30.0428L11.5774 30.0806C11.6642 30.1259 11.7488 30.1691 11.8356 30.2109L11.9278 30.2541L12.1155 30.3426L12.2041 30.3822L12.4153 30.4744L12.4894 30.5052C14.1307 31.1968 15.9344 31.5795 17.8267 31.5795C19.7189 31.5795 21.5226 31.1986 23.162 30.5052L23.2361 30.4744L23.4456 30.3822L23.5341 30.3426L23.7237 30.2541L23.8158 30.2109C23.9026 30.1694 23.9872 30.1277 24.074 30.0827L24.148 30.0449C24.2148 30.0108 24.2817 29.9745 24.3466 29.9403L24.4334 29.8935L24.4802 29.8663L25.2637 30.6497L24.9639 33.8796L24.9367 34.1758L24.7109 34.2735L24.6698 34.2877C24.4929 34.3599 24.3176 34.4303 24.139 34.499L24.0308 34.5386C23.9476 34.5694 23.8612 34.5999 23.7762 34.6307L23.6729 34.6667L23.4274 34.7498L23.292 34.7949C23.2107 34.8221 23.1276 34.8472 23.0466 34.8725L23.0356 34.8763ZM10.9525 29.7415L10.6943 29.5898C10.7793 29.6403 10.866 29.6926 10.9525 29.7415ZM5.27945 23.4709L5.15673 23.191C5.19633 23.285 5.23804 23.379 5.27945 23.4709ZM4.13838 19.2045L4.11118 18.9029C4.12024 19.0042 4.12931 19.1036 4.13838 19.2045ZM21.3597 19.1341L29.8098 27.5838L33.0398 27.2821L39 33.2442L33.7332 33.7335L33.2421 39L27.2819 33.0401L27.5817 29.8102L19.1332 21.3606C17.7953 21.8554 16.2335 21.5664 15.1574 20.4922C13.6841 19.019 13.6841 16.632 15.1574 15.1607C16.6289 13.6893 19.0159 13.6893 20.4892 15.1607C21.5649 16.2346 21.8557 17.7982 21.3594 19.1342L21.3597 19.1341Z"
      fill="#FF8787"
    />
  </Svg>
);

const PersonSVG = () => (
  <Svg width="22" height="22" viewBox="0 0 22 22">
    <Path
      fillRule="evenodd"
      d="M12.0586 2.15333C12.0586 3.34259 11.1909 4.30667 10.1206 4.30667C9.05029 4.30667 8.18262 3.34259 8.18262 2.15333C8.18262 0.96408 9.05029 0 10.1206 0C11.1909 0 12.0586 0.96408 12.0586 2.15333ZM11.8045 5.08257C13.1172 6.67381 14.6782 8.03186 16.5776 9.03325C17.9129 9.73971 16.9838 11.7905 15.6298 11.0738C14.2719 10.357 13.0533 9.52365 11.9512 8.55999C11.9362 9.87346 11.9851 11.1835 12.1129 12.497C12.1317 12.6513 12.2408 13.1314 12.286 13.2549C13.0683 15.388 13.8507 17.521 14.6368 19.6541C15.1596 21.0739 12.7411 21.8147 12.2183 20.3915C11.859 19.4157 11.5007 18.4399 11.1424 17.4641C10.7842 16.4886 10.426 15.513 10.0668 14.5375C10.0066 14.5409 9.95019 14.5409 9.89377 14.5375C8.77291 16.5197 8.05074 18.3921 7.306 20.7035C7.15178 21.1768 6.69667 21.4957 6.18513 21.53C6.03468 21.5403 5.88046 21.5266 5.73001 21.4855C5.0605 21.3071 4.67309 20.6624 4.87244 20.052C5.67735 17.5451 6.47475 15.4977 7.71974 13.3303C7.87395 13.056 7.95294 12.7542 7.94166 12.4455C7.91157 11.5196 7.84763 9.45507 7.86643 8.31307C7.36618 8.85149 6.91482 9.39334 6.51612 9.93519C6.28292 10.2507 5.89927 10.4222 5.51186 10.4222C5.28242 10.4222 5.04922 10.3639 4.84234 10.237C4.28943 9.90089 4.13898 9.21501 4.51135 8.71088C5.42159 7.4763 6.56502 6.24514 7.91157 5.04827C8.79924 4.08118 10.9808 4.02631 11.8045 5.08257Z"
      fill="#FEAF05"
    />
  </Svg>
);

const ClicksSVG = () => (
  <Svg width="29" height="29" viewBox="0 0 29 29">
    <G clip-path="url(#clip0_69_93)">
      <Path
        d="M14.5 29C22.5081 29 29 22.5081 29 14.5C29 6.49187 22.5081 0 14.5 0C6.49187 0 0 6.49187 0 14.5C0 22.5081 6.49187 29 14.5 29Z"
        fill="#F3FDFF"
      />
      <Path
        d="M10.7395 9.97908C11.0194 9.69916 11.0194 9.2618 10.7395 8.99945L9.23499 7.49498C8.95506 7.21505 8.5177 7.21505 8.25535 7.49498C7.97543 7.7749 7.97543 8.21226 8.25535 8.47461L9.75983 9.97908C10.0222 10.259 10.4595 10.259 10.7395 9.97908Z"
        fill="#2CCCEF"
      />
      <Path
        d="M9.11225 14.3351C9.30466 14.3351 9.47968 14.2652 9.60208 14.1252C9.74203 13.9852 9.81196 13.8104 9.81196 13.6353C9.81196 13.2505 9.49714 12.9357 9.11228 12.9357L6.97803 12.9355C6.61056 12.9355 6.27832 13.268 6.27832 13.6352C6.27832 13.8102 6.34825 13.9852 6.47077 14.1251C6.59316 14.265 6.78558 14.3351 6.97803 14.3351H9.11225Z"
        fill="#2CCCEF"
      />
      <Path
        d="M15.0597 9.64668C15.2172 9.5592 15.3397 9.41923 15.3921 9.22681L16.0045 7.18004C16.1094 6.8126 15.8996 6.42786 15.5321 6.30534C15.1647 6.20038 14.7799 6.41028 14.6574 6.77768L14.0451 8.82449C13.9401 9.19192 14.15 9.57666 14.5174 9.69918C14.6923 9.75165 14.8848 9.73416 15.0597 9.64668Z"
        fill="#2CCCEF"
      />
      <Path
        d="M20.9554 16.2069L13.1532 11.7634C12.9958 11.6759 12.8033 11.6935 12.6634 11.7809C12.5234 11.8858 12.4535 12.0608 12.4884 12.2357L14.1152 21.1751C14.1503 21.35 14.2726 21.4899 14.4301 21.5249C14.605 21.5774 14.7799 21.5249 14.8849 21.3849L16.4418 19.6181L18.786 22.8719C18.8559 22.9769 18.961 23.0294 19.0834 23.0469H19.1883C19.2758 23.0469 19.3457 23.0119 19.4158 22.9595L21.0426 21.7699C21.2351 21.63 21.2875 21.35 21.1476 21.1576L18.9436 17.9737L20.9205 17.0115C21.0779 16.9416 21.1654 16.784 21.1654 16.6266C21.1829 16.4516 21.0954 16.2943 20.9554 16.2069Z"
        fill="#2CCCEF"
      />
    </G>
    <Defs>
      <ClipPath id="clip0_69_93">
        <Rect width="29" height="29" fill="white" x="" y="" />
      </ClipPath>
    </Defs>
  </Svg>
);

const ImpressionsSVG = () => (
  <Svg width="29" height="29" viewBox="0 0 29 29">
    <G clip-path="url(#clip0_69_89)">
      <Path
        d="M14.5 29C22.5081 29 29 22.5081 29 14.5C29 6.49187 22.5081 0 14.5 0C6.49187 0 0 6.49187 0 14.5C0 22.5081 6.49187 29 14.5 29Z"
        fill="#FCF4FF"
      />
      <Path
        d="M5.38135 14.581C5.45471 14.4938 5.51831 14.3919 5.60145 14.3192C8.20855 12.1667 10.8156 10.0141 13.4276 7.8615C14.0537 7.34273 14.7287 7.3476 15.3548 7.8615C16.1716 8.53537 16.9836 9.20928 17.7956 9.88316C17.8396 9.92196 17.8836 9.95589 17.957 10.0092C17.9618 9.9268 17.9667 9.87347 17.9667 9.82013C17.9667 9.20444 17.9667 8.58871 17.9667 7.97301C17.9667 7.61909 18.0841 7.50758 18.4412 7.50758C19.082 7.50758 19.7178 7.50758 20.3586 7.50758C20.701 7.50758 20.8233 7.63362 20.8233 7.97301C20.8233 9.38382 20.8233 10.7995 20.8184 12.2103C20.8184 12.3412 20.8575 12.4236 20.9602 12.5012C21.6939 13.0975 22.4227 13.7035 23.1516 14.3047C23.4499 14.5471 23.4646 14.7168 23.22 15.0077C23.0391 15.221 22.8581 15.4343 22.6771 15.6476C22.4765 15.8852 22.2809 15.8997 22.0412 15.7009C19.5662 13.655 17.0863 11.6091 14.6113 9.5632C14.5379 9.50502 14.4694 9.44684 14.3814 9.3741C14.1026 9.60682 13.8238 9.83469 13.545 10.0625C11.2754 11.9388 9.00581 13.815 6.73625 15.6864C6.47701 15.8997 6.29114 15.8852 6.07591 15.6331C5.84604 15.3567 5.61613 15.0707 5.38135 14.7944C5.38135 14.7216 5.38135 14.6537 5.38135 14.581Z"
        fill="#BC2CEF"
      />
      <Path
        d="M15.8247 21.6789C15.8247 20.2681 15.8247 18.867 15.8247 17.4513C14.8661 17.4513 13.922 17.4513 12.9584 17.4513C12.9584 18.8573 12.9584 20.2681 12.9584 21.6935C12.8704 21.6935 12.8068 21.6935 12.7432 21.6935C11.4323 21.6935 10.1166 21.6935 8.80569 21.6935C8.23341 21.6935 7.95459 21.4171 7.95459 20.8499C7.95459 19.1627 7.95459 17.4804 7.95459 15.7933C7.95459 15.6769 7.97905 15.5897 8.0769 15.5072C10.1312 13.8346 12.1856 12.1523 14.24 10.4749C14.2889 10.4361 14.3378 10.3973 14.4014 10.3536C14.8612 10.7318 15.3209 11.1003 15.7758 11.4736C17.4193 12.8165 19.0579 14.1595 20.7014 15.4975C20.7944 15.5751 20.8384 15.6527 20.8384 15.7739C20.8335 17.4901 20.8384 19.2015 20.8335 20.9178C20.8335 21.3832 20.5351 21.6886 20.0655 21.6886C18.696 21.6935 17.3215 21.6886 15.9519 21.6886C15.9079 21.6886 15.8737 21.6838 15.8247 21.6789Z"
        fill="#BC2CEF"
      />
    </G>
    <Defs>
      <ClipPath id="clip0_69_89">
        <Rect width="29" height="29" fill="white" x="" y="" />
      </ClipPath>
    </Defs>
  </Svg>
);

const CtrSVG = () => (
  <Svg width="29" height="29" viewBox="0 0 29 29">
    <G clip-path="url(#clip0_69_80)">
      <Path
        d="M14.5 29C22.5081 29 29 22.5081 29 14.5C29 6.49187 22.5081 0 14.5 0C6.49187 0 0 6.49187 0 14.5C0 22.5081 6.49187 29 14.5 29Z"
        fill="#FFF5FC"
      />
      <Path
        d="M20.8025 15.8328C20.6812 15.7351 20.5262 15.6879 20.3712 15.7048C20.2162 15.7216 20.0746 15.7958 19.9769 15.9171L14.6692 22.4009C14.5681 22.5222 14.5209 22.6772 14.5378 22.8323C14.5546 22.9873 14.6288 23.1322 14.7534 23.2299C14.8579 23.3175 14.9894 23.3681 15.1242 23.3715C15.2994 23.3715 15.4645 23.2939 15.5791 23.1591L20.8868 16.6719C20.9913 16.5506 21.0384 16.3923 21.0249 16.2339C21.0081 16.0788 20.9306 15.9306 20.8025 15.8328Z"
        fill="#EF2CB8"
      />
      <Path
        d="M19.842 19.8363C19.3736 19.8363 18.922 20.0217 18.5917 20.3553C18.2615 20.6856 18.0728 21.1371 18.0728 21.6055C18.0728 22.074 18.2581 22.5255 18.5917 22.8558C18.922 23.186 19.3736 23.3748 19.842 23.3748C20.3104 23.3748 20.762 23.1894 21.0923 22.8558C21.4225 22.5255 21.6112 22.074 21.6112 21.6055C21.6112 21.1371 21.4259 20.6856 21.0923 20.3553C20.762 20.0217 20.3104 19.8363 19.842 19.8363ZM19.842 22.1953C19.687 22.1953 19.5353 22.1346 19.4241 22.0234C19.3129 21.9122 19.2522 21.7639 19.2522 21.6055C19.2522 21.4505 19.3129 21.2989 19.4241 21.1877C19.5353 21.0765 19.6836 21.0158 19.842 21.0158C20.0004 21.0158 20.1486 21.0765 20.2599 21.1877C20.3711 21.2989 20.4317 21.4471 20.4317 21.6055C20.4317 21.7606 20.3711 21.9122 20.2599 22.0234C20.1486 22.1346 20.0004 22.1953 19.842 22.1953Z"
        fill="#EF2CB8"
      />
      <Path
        d="M12.1752 7.18555C12.3302 7.18555 12.4818 7.12489 12.593 7.01367C12.7042 6.90246 12.7649 6.75417 12.7649 6.5958V6.27229C12.7649 6.05996 12.6537 5.86787 12.4684 5.76004C12.283 5.6522 12.0606 5.65558 11.8786 5.76004C11.6966 5.8645 11.582 6.05996 11.582 6.27229V6.5958C11.582 6.75082 11.6427 6.90246 11.7539 7.01367C11.8685 7.12489 12.0201 7.18555 12.1752 7.18555Z"
        fill="#EF2CB8"
      />
      <Path
        d="M14.0221 7.95384C14.1805 7.95384 14.3322 7.88983 14.44 7.77861L14.6691 7.54945C14.8174 7.40116 14.8747 7.18214 14.8208 6.97992C14.7669 6.77772 14.6051 6.61933 14.4029 6.56543C14.2007 6.51149 13.9817 6.57215 13.8334 6.72044L13.6043 6.95633C13.493 7.06754 13.4324 7.21583 13.4324 7.37085C13.4324 7.52586 13.493 7.6775 13.6043 7.78534C13.7155 7.89318 13.8637 7.95384 14.0221 7.95384Z"
        fill="#EF2CB8"
      />
      <Path
        d="M9.91734 7.77534C10.0285 7.88317 10.1802 7.94384 10.3352 7.94384C10.4902 7.94384 10.6419 7.87983 10.7497 7.76523C10.8575 7.65402 10.9182 7.50238 10.9182 7.34736C10.9148 7.19235 10.8542 7.04068 10.7396 6.93284L10.5037 6.70368C10.352 6.55877 10.133 6.50149 9.93079 6.56215C9.7286 6.61943 9.57358 6.7812 9.51968 6.98678C9.46912 7.19235 9.52978 7.40802 9.68142 7.55293L9.91734 7.77534Z"
        fill="#EF2CB8"
      />
      <Path
        d="M18.3355 14.8521C18.103 14.4545 17.7525 14.1377 17.3346 13.9389L14.3455 12.9346C14.2242 12.8942 14.1197 12.8133 14.0456 12.7088C13.9748 12.601 13.9377 12.4763 13.9444 12.3449V10.397C13.9444 9.76347 13.6074 9.18048 13.0615 8.86369C12.5156 8.5469 11.8382 8.5469 11.2923 8.86369C10.7463 9.18048 10.4093 9.76347 10.4093 10.397V14.7982L9.8196 14.5994C9.33434 14.4309 8.7985 14.4848 8.35366 14.7409C7.90882 14.997 7.59879 15.4385 7.50444 15.9406C7.41008 16.4461 7.53813 16.9684 7.8583 17.3695L10.1869 20.3182C10.5206 20.7563 11.0395 21.0124 11.5888 21.0124H14.5376C14.7128 21.0124 14.8779 20.9349 14.9925 20.8001L15.6058 20.0621L15.7373 19.9037C15.7305 19.9037 15.7238 19.9037 15.7204 19.9037C15.0667 19.9037 14.4533 19.6509 13.9916 19.1893C13.53 18.7276 13.2772 18.1142 13.2772 17.4605C13.2772 16.8067 13.53 16.1934 13.9916 15.7317C14.4533 15.27 15.0667 15.0173 15.7204 15.0173C16.3742 15.0173 16.9875 15.27 17.4492 15.7317C17.8098 16.0923 18.0423 16.5438 18.1266 17.0325L18.5175 16.5641C18.6119 16.4528 18.659 16.3079 18.6523 16.163C18.6759 15.7081 18.568 15.2498 18.3355 14.8521Z"
        fill="#EF2CB8"
      />
      <Path
        d="M15.7138 15.6947C15.2454 15.6947 14.7938 15.8801 14.4636 16.2137C14.1333 16.5439 13.9446 16.9955 13.9446 17.4639C13.9446 17.9324 14.1299 18.3839 14.4636 18.7142C14.7938 19.0444 15.2454 19.2331 15.7138 19.2331C16.1822 19.2331 16.6338 19.0478 16.964 18.7142C17.2943 18.3839 17.483 17.9324 17.483 17.4639C17.483 16.9955 17.2977 16.5439 16.964 16.2137C16.6338 15.8834 16.1822 15.6947 15.7138 15.6947ZM15.7138 18.0537C15.5588 18.0537 15.4071 17.993 15.2959 17.8818C15.1847 17.7706 15.1241 17.6223 15.1241 17.4639C15.1241 17.3055 15.1847 17.1573 15.2959 17.046C15.4071 16.9349 15.5554 16.8742 15.7138 16.8742C15.8722 16.8742 16.0205 16.9349 16.1317 17.046C16.2429 17.1573 16.3035 17.3055 16.3035 17.4639C16.3035 17.6223 16.2429 17.7706 16.1317 17.8818C16.0205 17.993 15.8722 18.0537 15.7138 18.0537Z"
        fill="#EF2CB8"
      />
    </G>
    <Defs>
      <ClipPath id="clip0_69_80">
        <Rect width="29" height="29" fill="white" x="" y="" />
      </ClipPath>
    </Defs>
  </Svg>
);

type CampaignPDFProps = { campaign: CampaignDetailsType };

const CampaignPDF: FC<CampaignPDFProps> = ({ campaign }) => {
  const progressPercent = () => {
    if ((campaign?.served_days / campaign?.total_days) * 100 > 100) return 100;
    if ((campaign?.served_days / campaign?.total_days) * 100 < 0) return 0;
    return (campaign?.served_days / campaign?.total_days) * 100 || 0;
  };

  const audiences = campaign?.orderlines[0]?.targets;

  return (
    <Document>
      <Page style={styles.page} size="A4">
        <View style={styles.header}>
          <View style={styles.downloaded_at}>
            <Text style={styles.downloaded_at_text}>
              PDF downloaded at {moment().format('MM/DD/YYYY, hh:mm A')}
            </Text>
          </View>
        </View>
        <View style={styles.campaign}>
          <View style={styles.campaign_header}>
            <View>
              <Text style={styles.campaign_name}>{campaign?.name}</Text>
            </View>
          </View>
          <View style={styles.campaign_details}>
            <View style={styles.audiences}>
              {audiences.map(audience => {
                return TargetsTypes.includes(audience.type) ? (
                  <View key={audience.id} style={styles.individual_audience}>
                    <View style={styles.individual_audience_left}>
                      <View style={styles.individual_audience_targetview}>
                        <TargetSVG />
                      </View>
                      <View style={styles.individual_audience_nameview}>
                        <Text style={styles.individual_audience_name}>{audience.beewo.name}</Text>
                      </View>
                    </View>
                    <View style={styles.individual_audience_right}>
                      <View style={styles.individual_audience_personview}>
                        <PersonSVG />
                      </View>
                      <View style={styles.individual_audience_prospectsview}>
                        <Text style={styles.individual_audience_prospects}>
                          Prospects ({audience?.beewo?.prospects_counts})
                        </Text>
                      </View>
                    </View>
                  </View>
                ) : (
                  <View />
                );
              })}
            </View>
            <View style={styles.campaign_info}>
              <View style={styles.campaign_info_left}>
                <View style={styles.campaign_info_creatives}>
                  <View style={styles.campaign_info_creatives_label}>
                    <Text style={styles.campaign_info_creatives_label_text}>Created:</Text>
                  </View>
                  <View>
                    <Text style={styles.campaign_info_creatives_value_text}>
                      {moment(campaign.created).format('MM/DD/YYYY, hh:mm')}
                    </Text>
                  </View>
                </View>
                <View style={styles.campaign_info_creatives}>
                  <View style={styles.campaign_info_creatives_label}>
                    <Text style={styles.campaign_info_creatives_label_text}>Creative:</Text>
                  </View>
                  <View>
                    <Text style={styles.campaign_info_creatives_value_text}>
                      {campaign.orderlines
                        .map(orderline => orderline.creative_type)
                        .filter((url, i, array) => array.indexOf(url) === i)
                        .join(', ')}
                    </Text>
                  </View>
                </View>
                <View style={styles.campaign_info_creatives}>
                  <View style={styles.campaign_info_creatives_label}>
                    <Text style={styles.campaign_info_creatives_label_text}>
                      Click Through Link:
                    </Text>
                  </View>
                  <View>
                    {campaign?.orderlines.map(orderline => (
                      <Text
                        key={orderline.eltoro_id}
                        style={styles.campaign_info_creatives_value_text}
                      >
                        {orderline.clickThroughUrl}
                      </Text>
                    ))}
                  </View>
                </View>
              </View>
              <View style={styles.campaign_info_days}>
                <View style={styles.campaign_info_days_wrapper}>
                  <View style={styles.campaign_info_days_header}>
                    <View>
                      <Text style={styles.campaign_info_days_header_label}>Days </Text>
                    </View>
                    <View>
                      <Text style={styles.campaign_info_days_header_value}>
                        {campaign?.served_days} of {campaign?.total_days}
                      </Text>
                    </View>
                  </View>
                  <View style={styles.campaign_info_days_progress_meter}>
                    <Svg
                      style={{
                        borderRadius: '30px',
                        width: `${progressPercent()}%`,
                        height: '100%',
                      }}
                      preserveAspectRatio="none"
                      viewBox="0 0 100 100"
                    >
                      <Defs>
                        <LinearGradient id="radialGradient" x1="" y1="" x2="" y2="">
                          <Stop offset="0%" stopColor="#ffab03" />
                          <Stop offset="72.9%" stopColor="#fcd917" />
                        </LinearGradient>
                      </Defs>

                      <Rect width="100%" height="100%" fill="url(#radialGradient)" x="1" y="1" />
                    </Svg>
                  </View>
                  <View style={styles.start_end_date}>
                    <View>
                      <Text style={styles.flight_text}>Flight Date Start</Text>
                    </View>
                    <View>
                      <Text style={styles.flight_text}>Flight Date End</Text>
                    </View>
                  </View>
                </View>
              </View>
            </View>
          </View>
        </View>
        <View style={{ ...styles.campaign, marginTop: '20px' }}>
          <View style={styles.campaign_header}>
            <View>
              <Text style={styles.campaign_name}>{campaign?.name}</Text>
            </View>
          </View>
          <View style={styles.campaign_report}>
            <View style={styles.campaign_report_block}>
              <View style={styles.campaign_report_block_sm}>
                <View style={styles.campaign_report_block_sm_icon}>
                  <ClicksSVG />
                </View>
                <View style={styles.campaign_report_block_sm_clicks}>
                  <View>
                    <Text style={styles.campaign_report_block_sm_clicks_count}>0</Text>
                  </View>
                  <View>
                    <Text style={styles.campaign_report_block_sm_clicks_title}>Clicks</Text>
                  </View>
                </View>
              </View>
              <View style={styles.campaign_report_block_sm}>
                <View style={styles.campaign_report_block_sm_icon}>
                  <ImpressionsSVG />
                </View>
                <View style={styles.campaign_report_block_sm_clicks}>
                  <View>
                    <Text style={styles.campaign_report_block_sm_clicks_count}>0</Text>
                  </View>
                  <View>
                    <Text style={styles.campaign_report_block_sm_clicks_title}>Impressions</Text>
                  </View>
                </View>
              </View>
              <View style={styles.campaign_report_block_sm}>
                <View style={styles.campaign_report_block_sm_icon}>
                  <CtrSVG />
                </View>
                <View style={styles.campaign_report_block_sm_clicks}>
                  <View>
                    <Text style={styles.campaign_report_block_sm_clicks_count}>0</Text>
                  </View>
                  <View>
                    <Text style={styles.campaign_report_block_sm_clicks_title}>CTR</Text>
                  </View>
                </View>
              </View>
            </View>
            <View style={{ ...styles.campaign_report_block, marginTop: '20px' }}>
              <View style={{ width: '46%' }}>
                <View style={styles.campaign_report_block_lg}>
                  <View style={{ marginBottom: '4.5px' }}>
                    <Text style={styles.campaign_report_total}>{campaign?.total_impressions}</Text>
                  </View>
                  <View style={{ marginBottom: '3.7px' }}>
                    <Text style={{ fontSize: '12px', color: '#3D3D3D' }}>Total Impressions</Text>
                  </View>
                  <View style={{ marginBottom: '2.5px' }}>
                    <Text style={styles.campaign_report_details}>
                      Served: {campaign?.served_impressions} Impressions
                    </Text>
                  </View>
                  <View>
                    <Text style={styles.campaign_report_details}>
                      Remaining: {campaign?.total_impressions - campaign?.served_impressions}{' '}
                      Impressions
                    </Text>
                  </View>
                </View>
              </View>
              <View style={{ width: '46%' }}>
                <View style={styles.campaign_report_block_lg}>
                  <View style={{ marginBottom: '4.5px' }}>
                    <Text style={styles.campaign_report_total}>${campaign.total_rate}</Text>
                  </View>
                  <View style={{ marginBottom: '3.7px' }}>
                    <Text style={{ fontSize: '12px', color: '#3D3D3D' }}>Total Budget</Text>
                  </View>
                  <View style={{ marginBottom: '2.5px' }}>
                    <Text style={styles.campaign_report_details}>
                      Spent: ${campaign.served_rate}
                    </Text>
                  </View>
                  <View>
                    <Text style={styles.campaign_report_details}>
                      Remaining: ${campaign.total_rate - campaign.served_rate}
                    </Text>
                  </View>
                </View>
              </View>
            </View>
          </View>
        </View>
        <View style={{ ...styles.campaign, marginTop: '20px' }}>
          <View style={styles.campaign_header}>
            <View>
              <Text style={styles.campaign_name}>{campaign?.name}</Text>
            </View>
          </View>
          <View style={styles.campaign_creatives}>
            {campaign?.orderlines?.map((campaignDetails: any) => {
              return campaignDetails?.creatives?.map((creative: any, index: number) => {
                return (
                  <View key={index} style={styles.campaign_individual_creative}>
                    <View style={styles.campaign_individual_creative_name_wr}>
                      <Text
                        style={{
                          ...styles.campaign_individual_creative_name,
                          marginRight: '5px',
                        }}
                      >
                        {creative.name.split('__')[1]}
                      </Text>
                      <Text>
                        {campaignDetails.creative_type === 'Banner' &&
                        creative?.width &&
                        creative?.height
                          ? `${campaignDetails.creative_type} - ${creative.width}x${creative.height}`
                          : campaignDetails.creative_type}
                      </Text>
                    </View>
                    <View style={styles.campaign_individual_creative_stats}>
                      <View style={styles.campaign_individual_creative_stats_ind}>
                        <View style={styles.campaign_individual_creative_stats_ind_icon}>
                          <ClicksSVG />
                        </View>
                        <View style={styles.campaign_individual_creative_stats_ind_content}>
                          <Text style={styles.campaign_individual_creative_stats_ind_title}>
                            Click:
                          </Text>
                          <Text style={styles.campaign_individual_creative_stats_ind_value}>
                            {creative.click}
                          </Text>
                        </View>
                      </View>
                      <View style={styles.campaign_individual_creative_stats_ind}>
                        <View style={styles.campaign_individual_creative_stats_ind_icon}>
                          <ImpressionsSVG />
                        </View>
                        <View style={styles.campaign_individual_creative_stats_ind_content}>
                          <Text style={styles.campaign_individual_creative_stats_ind_title}>
                            Impressions:
                          </Text>
                          <Text style={styles.campaign_individual_creative_stats_ind_value}>
                            {creative.impressions}
                          </Text>
                        </View>
                      </View>
                      <View style={styles.campaign_individual_creative_stats_ind}>
                        <View style={styles.campaign_individual_creative_stats_ind_icon}>
                          <CtrSVG />
                        </View>
                        <View style={styles.campaign_individual_creative_stats_ind_content}>
                          <Text style={styles.campaign_individual_creative_stats_ind_title}>
                            CTR:
                          </Text>
                          <Text style={styles.campaign_individual_creative_stats_ind_value}>
                            {creative.ctr}%
                          </Text>
                        </View>
                      </View>
                    </View>
                  </View>
                );
              });
            })}
          </View>
        </View>
      </Page>
    </Document>
  );
};
export default CampaignPDF;
